<template>
  <Container class="items-center gap-2.5 md:gap-5">
    <h1
      class="w-83.75 text-center text-[26px] leading-[115%] md:w-150.75 md:text-4.5xl lg:w-195 xl:w-281.5 xl:text-6xl 2xl:w-331.5 2xl:text-[70px]/[70px]"
    >
      {{ publication?.title }}
    </h1>

    <div class="flex items-center gap-2.5">
      <TypeBadge
        v-for="category in publication?.publicationTags"
        :key="category?.id"
        :text="category?.name"
        class="xl:text-[17px]"
      />
    </div>
  </Container>

  <Container
    class="mt-10 items-center gap-17.5 sm:gap-15 md:mt-15 xl:mt-17.5 xl:gap-17.5 2xl:gap-20"
  >
    <div
      class="relative flex w-55 items-center justify-center sm:w-57.25 md:w-78.5 lg:w-90 xl:w-101.25! 2xl:w-118!"
    >
      <NuxtImg
        :src="publication?.image?.url"
        format="webp"
        sizes="xs:220px sm:230px md:315px lg:360px xl:405px xxl:472px"
        class="h-auto w-full object-contain shadow-[0_5px_22px_rgba(0,0,0,0.2)] sm:shadow-[0_5.219px_22.962px_rgba(0,0,0,0.2)] md:shadow-[0_7.757px_31.027px_rgba(0,0,0,0.2)] lg:shadow-[0_8.91px_35.64px_rgba(0,0,0,0.2)] min-[1440px]:shadow-[0_10px_40px_rgba(0,0,0,0.2)] min-[1680px]:shadow-[0_12px_47px_rgba(0,0,0,0.2)]"
        :preload="{ fetchPriority: 'high' }"
        :alt="
          publication?.image?.caption ||
          publication?.image?.media?.file_name ||
          'publication image'
        "
      />
    </div>

    <table
      class="mb-10 w-full md:mb-15 md:w-151.25 lg:w-153.75 xl:mb-17.5 xl:w-185.25! 2xl:mb-20 2xl:w-216.25!"
    >
      <tbody>
        <template v-for="item in publicationDetails" :key="item.id">
          <tr
            v-if="item.content"
            class="h-max border-t border-gray text-base leading-[113%] last:border-b md:text-[17px] xl:text-xl xl:leading-[115%] [&_td]:py-2.5"
          >
            <td class="w-30 md:w-50">{{ item.label }}</td>
            <td class="max-w-53.75">{{ item.content }}</td>
          </tr>
        </template>
      </tbody>
    </table>
  </Container>

  <DynamicContentResolver :blocks="publication?.content_blocks" />

  <Container
    v-if="publication?.price || publication?.eshop_link"
    class="mx-auto mb-30 md:mb-37.5 lg:mb-37.5 lg:w-194.5 lg:px-0! xl:mb-42.5 xl:w-223.25 2xl:mb-47.5! 2xl:w-263.5"
  >
    <div
      class="mt-20 flex flex-col items-center justify-center gap-5 md:flex-row lg:mt-25 xl:mt-30 xl:gap-10"
    >
      <div
        v-if="publication?.price"
        class="flex w-full items-center justify-between border p-2.5 md:max-w-1/2 xl:text-xl xl:leading-[120%]"
      >
        <span>{{ t('common.recommended_price') }}</span>
        <span>{{ publication?.price }} {{ t('common.czk') }}</span>
      </div>

      <NuxtLink
        v-if="publication?.eshop_link"
        :to="publication?.eshop_link"
        target="_blank"
        :aria-label="t('common.buy')"
        class="w-full md:max-w-1/2"
      >
        <Button :title="t('common.buy')" />
      </NuxtLink>
    </div>
  </Container>
</template>

<script setup lang="ts">
import type { PageData, Publication } from '~/types'

const props = defineProps<{ pageData: Publication & PageData }>()

const { locale, t } = useI18n()
const setI18nParams = useSetI18nParams()

const breadcrumbsData = useBreadcrumbs()

const publication = computed(() => props.pageData)

useDynamicSeo(publication, {
  bodyAttrs: {
    style: 'background-color: #E4E4E4',
  },
})

watch(
  publication,
  (val) => {
    if (val) {
      setI18nParams({
        cs: { slug: useGetSlugSegments(val, 'cs') },
        en: { slug: useGetSlugSegments(val, 'en') },
      })
    }
  },
  { immediate: true },
)

onMounted(() => {
  breadcrumbsData.value = publication.value?.breadcrumbs[locale.value]
})

const publicationDetails = computed(() => [
  {
    id: 1,
    label: t('publications.author'),
    content: publication.value?.authors,
  },
  {
    id: 2,
    label: t('publications.year'),
    content: publication.value?.year,
  },
  {
    id: 3,
    label: t('publications.publisher'),
    content: publication.value?.publisher,
  },
  {
    id: 4,
    label: t('publications.place_of_publication'),
    content: publication.value?.place_of_publication,
  },
  {
    id: 5,
    label: t('publications.isbn'),
    content: publication.value?.isbn,
  },
  {
    id: 6,
    label: t('publications.edition'),
    content: publication.value?.edition,
  },
  {
    id: 7,
    label: t('publications.extent'),
    content: publication.value?.extent,
  },
  {
    id: 8,
    label: t('publications.language'),
    content: publication.value?.language,
  },
  {
    id: 9,
    label: t('publications.publication_type'),
    content: publication.value?.publication_type,
  },

  ...(publication.value?.info?.map((item: any, index: number) => ({
    id: 10 + index,
    label: item.name[locale.value],
    content: item.value[locale.value],
  })) || []),
])
</script>
